import { LanguageSelector } from '@/components/LanguageSelector';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../config';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { cleanupAllRecaptcha, cleanupRecaptcha, initializeRecaptcha, setGlobalConfirmationResult, setupPhoneAuth } from '../lib/firebase';
import { setStoredPhoneNumber, setVerificationId } from '../store/authSlice';
import { useAppDispatch } from '../store/hooks';
import { useLocalization } from '@/contexts/LocalizationContext';

export default function Login() {
  const { t } = useLocalization();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { storeSettings } = useStoreSettings();
  const [countryCode, setCountryCode] = useState('+852');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  // Handle reCAPTCHA callback
  const handleRecaptchaSolved = () => {
    setIsRecaptchaReady(true);
    setError(''); // Clear any previous errors
  };

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    let isMounted = true;

    const initRecaptcha = async () => {
      try {
        const containerId = isDesktop ? 'recaptcha-container-desktop' : 'recaptcha-container-mobile';

        // Clean up any existing reCAPTCHA first
        cleanupRecaptcha(containerId);

        // Wait a bit to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 300));

        if (isMounted) {
          await initializeRecaptcha(containerId, handleRecaptchaSolved);
          setIsRecaptchaReady(true); // reCAPTCHA v3 is ready immediately
        }
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
        if (isMounted) {
          setIsRecaptchaReady(true); // Allow proceeding even if reCAPTCHA fails
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initRecaptcha, 200);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      // Clean up all reCAPTCHA instances to be safe
      cleanupAllRecaptcha();
    };
  }, [isDesktop]);

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Add country code if not present
    if (digits.length === 10) {
      return `+1${digits}`; // US format
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    } else if (!digits.startsWith('1') && digits.length > 10) {
      return `+${digits}`;
    }

    return phone.startsWith('+') ? phone : `+${digits}`;
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError(t("auth.login.phonePlaceholder"));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formattedPhone = formatPhoneNumber(`${countryCode}${phoneNumber}`);
      console.log('Sending OTP to:', formattedPhone);

      // Use different container IDs for mobile and desktop
      const containerId = isDesktop ? 'recaptcha-container-desktop' : 'recaptcha-container-mobile';
      const confirmation = await setupPhoneAuth(formattedPhone, containerId, handleRecaptchaSolved);

      console.log('OTP sent successfully');

      // Store phone number in Redux and localStorage, confirmation result in module-level storage
      dispatch(setStoredPhoneNumber(formattedPhone));
      dispatch(setVerificationId(confirmation.verificationId));
      localStorage.setItem('temp_phone_number', formattedPhone);
      setGlobalConfirmationResult(confirmation);

      // Navigate to OTP page
      navigate(routes.otp);
    } catch (error: any) {
      console.error('OTP send error:', error);

      let errorMessage = t("auth.errorMessages.otp.failed")

      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = t("auth.errorMessages.otp.invalidPhoneNumber");
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t("auth.errorMessages.otp.tooManyRequests");
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100svh',
        height: '100svh',
        position: 'relative',
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
      }}
    >
      <Box sx={{ flex: isDesktop ? 0.3 : 0.5 }}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            color: 'white',
            pt: 4,
            pb: 4,
          }}
        >
          <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
        </Box>
      </Box>

      <Box sx={{ flex: 0.5 }}>

        <Box
          sx={{
            height: isDesktop ? 'auto' : '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: isDesktop ? 4 : 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: isDesktop ? '24px' : '24px 24px 0 0',
              background: '#FFF',
              backdropFilter: 'blur(10px)',
              p: 4,
              width: '100%',
              height: '100%',
              maxWidth: isDesktop ? '400px' : '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {/* Language Selector */}
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: 16,
                transform: 'translateY(-50%)',
                zIndex: 1,
                color: '#fff'
              }}
            >
              <LanguageSelector dropdownColor='#fff' />
            </Box>
            {/* Title with lines */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, #D9E4CE, transparent)'
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  mx: 2,
                  color: '#2C3E50',
                  fontWeight: 600,
                  fontSize: '18px',
                  textAlign: 'center',
                }}
              >
                {t("auth.login.title")}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, #D9E4CE, transparent)'
                }}
              />
            </Box>

            <form onSubmit={handleSendOTP}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#2C3E50',
                    mb: 1,
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  {t("auth.login.phoneLabel")}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Country Code Select */}
                  <FormControl sx={{ minWidth: 80 }}>
                    <Select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={isLoading}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiSelect-select': {
                          p: '10px'
                        }
                      }}
                      IconComponent={KeyboardArrowDown}
                    >
                      <MenuItem value="+852" sx={{ textAlign: 'left' }}>+852</MenuItem>
                      <MenuItem value="+1" sx={{ textAlign: 'left' }}>+1</MenuItem>
                      <MenuItem value="+44" sx={{ textAlign: 'left' }}>+44</MenuItem>
                      <MenuItem value="+91" sx={{ textAlign: 'left' }}>+91</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Phone Number Input */}
                  <TextField
                    fullWidth
                    type="tel"
                    placeholder={t("auth.login.phonePlaceholder")}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    error={!!error}
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#FFF',
                        border: '1px solid #5B5B5B',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiInputBase-input': {
                          padding: 2,
                          height: '60px',
                          boxSizing: 'border-box',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#A0A0A0',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || !phoneNumber || !isRecaptchaReady}
                  sx={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    textTransform: 'none',
                    backgroundColor: '#FF5413',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#d2450eff',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#999',
                    },
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                      {t("auth.messages.sendingCode")}
                    </>
                  ) : (
                    t("common.continue")
                  )}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
                  {error}
                </Alert>
              )}
            </form>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
} 